import { useMemo, useState } from "react";

import { cn } from "@/shared/libs/commonUtils";
import { isNull } from "@/shared/libs/type-utils";
import { useSearchParams } from "next/navigation";
import Flex from "../../common/Flex";
import List from "../../common/List";
import PhosphorIcon from "../../common/PhosphorIcon";
import type { FilterComponent } from "../filter.types";

const RatingFilter: FilterComponent<string> = ({
  applyFilter,
  currentValue,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const MAX_STARS = 5;

  const currentState = useSearchParams().get("stars") || "";
  const currentRating = Number.parseInt(currentState || "0");

  const stars = useMemo(
    () => Array.from({ length: MAX_STARS }, (_, i) => i),
    [],
  );

  const handleMouseEnter = (index: number) => () => setHoveredIndex(index);
  const handleMouseLeave = () => setHoveredIndex(null);

  const isStarFilled = (index: number) => {
    // If hovering, show hover state
    if (hoveredIndex !== null) {
      return index <= hoveredIndex;
    }
    // If pending rating (just clicked), show that
    if (currentValue) {
      return index < +currentValue;
    }
    // Otherwise show selected state
    return index < currentRating;
  };

  const handleClick = (starIndex: number) => {
    const newRating = starIndex + 1;

    if (currentRating === newRating) {
      applyFilter("")();
    } else {
      applyFilter(newRating.toString())();
    }
  };

  return (
    <Flex
      direction="row"
      alignItems="center"
      justifyContent="center"
      gap="3"
      className="pb-4"
      onMouseLeave={handleMouseLeave}
    >
      <List<number>
        items={stars}
        renderItems={(starIndex) => (
          <PhosphorIcon
            name="Star"
            key={starIndex}
            onMouseEnter={handleMouseEnter(starIndex)}
            size={24}
            weight={isStarFilled(starIndex) ? "fill" : "regular"}
            className={cn(
              "text-yellow-500 hover:fill-yellow-500 cursor-pointer weight-bold",
              starIndex === 0 && isNull(hoveredIndex)
                ? "animate-pulse duration-100"
                : "",
            )}
            onClick={() => handleClick(starIndex)}
          />
        )}
      />
    </Flex>
  );
};

export default RatingFilter;
